/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get("sticky-atc")) {
  customElements.whenDefined("product-form").then(() => {
    class StickyAtc extends customElements.get("product-form") {
      constructor() {
        super();

        const idInput = this.form?.querySelector('[name="id"]');
        if (idInput) idInput.required = false;

        this.section = this.closest(".section-main-product");
        this.mainProductForm = this.section.querySelector(".js-product");
        this.buyButtons = this.section.querySelector("buy-buttons");
        this.variantPicker = this.section.querySelector("variant-picker");
        this.stickyPriceCurrent = this.querySelector(".sticky-atc__price-current");
        this.stickyPriceWas = this.querySelector(".sticky-atc__price-was");

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener("scroll", this.throttledOnScroll);

        this.boundOnVariantChange = this.onVariantChange.bind(this);
        this.section.addEventListener("on:variant:change", this.boundOnVariantChange);

        // Forward sticky option changes to the main variant picker
        this.stickyOptionListeners = [];
        this.querySelectorAll(".sticky-atc__option").forEach((optionEl) => {
          const callback = this.onStickyOptionChange.bind(this);
          optionEl.addEventListener("change", callback);
          this.stickyOptionListeners.push({ optionEl, callback });
        });

        this.syncPrice();
        this.syncButtonStyles();
      }

      disconnectedCallback() {
        window.removeEventListener("scroll", this.throttledOnScroll);
        this.section?.removeEventListener(
          "on:variant:change",
          this.boundOnVariantChange
        );
        this.stickyOptionListeners?.forEach(({ optionEl, callback }) => {
          optionEl.removeEventListener("change", callback);
        });
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector(
          'product-form buy-buttons input[name="id"]'
        ).value;
        if (!variantId) {
          const target = this.section.querySelector("#product-info");
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data from main form (except fields already in sticky form)
        this.form
          .querySelectorAll("[data-copied]")
          .forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement("input");
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute("data-copied", "");
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector(".pageheader--sticky")
          ? document.querySelector(".section-header").offsetHeight
          : 0;

        const shouldShow =
          this.buyButtons.getBoundingClientRect().bottom <= topOffset;

        if (shouldShow) {
          this.classList.add("sticky-atc--top");
          this.classList.remove("sticky-atc--bottom");
        }

        this.classList.toggle("sticky-atc--out", !shouldShow);
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');
        if (idInput) idInput.value = evt.detail.variant?.id ?? "";

        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
        this.syncPrice();
        this.syncStickyVariants(evt.detail.variant);
      }

      /**
       * Handle a change in a sticky option dropdown — forward to main variant picker.
       * @param {Event} evt - Change event from custom-select.
       */
      onStickyOptionChange(evt) {
        if (!this.variantPicker) return;
        const optionEl = evt.currentTarget;
        const optionName = optionEl.dataset.optionName;
        const value = evt.detail?.selectedValue;
        if (!optionName || !value) return;

        const selectors = this.variantPicker.querySelectorAll(".option-selector");
        const selector = Array.from(selectors).find(
          (el) => el.dataset.option === optionName
        );
        if (!selector) return;

        StickyAtc.selectMainOption(selector, value);
      }

      /**
       * Syncs the sticky price display from the main product price element.
       */
      syncPrice() {
        if (!this.stickyPriceCurrent) return;
        const priceEl = this.section.querySelector(".price");
        if (!priceEl) return;
        const current = priceEl.querySelector(".price__current");
        const compare = priceEl.querySelector(".price__compare");
        if (current) this.stickyPriceCurrent.innerHTML = current.innerHTML;
        if (this.stickyPriceWas && compare) {
          this.stickyPriceWas.innerHTML = compare.innerHTML;
          this.stickyPriceWas.hidden = compare.classList.contains("hidden");
        }
      }

      /**
       * Copies button CSS custom properties from the main ATC button to the sticky button.
       */
      syncButtonStyles() {
        if (!this.submitBtn) return;
        const mainBtn = this.mainProductForm.querySelector('[name="add"]');
        if (!mainBtn) return;
        const computedStyles = window.getComputedStyle(mainBtn);
        [
          "--btn-text-color",
          "--btn-text-hover-color",
          "--btn-bg-color",
          "--btn-bg-hover-color",
          "--btn-border-color",
          "--btn-border-hover-color",
          "--btn-border-radius",
        ].forEach((variable) => {
          const value = computedStyles.getPropertyValue(variable);
          if (value.trim()) {
            this.submitBtn.style.setProperty(variable, value.trim());
          } else {
            this.submitBtn.style.removeProperty(variable);
          }
        });
      }

      /**
       * Updates sticky option dropdowns to match the given variant's options.
       * @param {object|null} variant
       */
      syncStickyVariants(variant) {
        if (!variant) return;
        this.querySelectorAll(".sticky-atc__option").forEach((optionEl) => {
          const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
          if (Number.isNaN(optionIndex)) return;
          const value = variant.options[optionIndex];
          if (value === undefined) return;
          const customSelectEl = optionEl.querySelector("custom-select");
          if (customSelectEl) StickyAtc.setCustomSelectValue(customSelectEl, value);
        });
      }

      /**
       * Programmatically sets a custom-select value without firing a change event.
       * @param {Element} customSelectEl
       * @param {string} value
       */
      static setCustomSelectValue(customSelectEl, value) {
        const options = customSelectEl.querySelectorAll(".custom-select__option");
        const option = Array.from(options).find((li) => li.dataset.value === value);
        if (!option) return;
        customSelectEl.selectOption(option, false, false);
      }

      /**
       * Updates a main variant picker option selector to the given value.
       * @param {Element} selector - .option-selector element in the main variant picker.
       * @param {string} value
       */
      static selectMainOption(selector, value) {
        if (selector.dataset.selectorType === "dropdown") {
          const customSelect = selector.querySelector("custom-select");
          if (!customSelect) return;
          const options = customSelect.querySelectorAll(".custom-select__option");
          const targetOption = Array.from(options).find(
            (li) => li.dataset.value === value
          );
          if (!targetOption) return;
          customSelect.selectOption(targetOption);
        } else {
          // Radio buttons
          const inputs = selector.querySelectorAll("input.js-option");
          const input = Array.from(inputs).find((el) => el.value === value);
          if (input && !input.checked) input.click();
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock
          : this.submitBtn.dataset.unavailableText;

        this.submitBtn.disabled = !variantAvailable;
        this.submitBtn.setAttribute(
          "aria-disabled",
          variantAvailable ? "false" : "true"
        );
        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define("sticky-atc", StickyAtc);
  });
}
